// Types
// Components
import { useCallback, useEffect, useMemo } from 'react';
import TreeComponent from 'components/TreeComponent';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useExpandable } from 'hooks/useExpandable';
// Utils
import { useRowProps } from 'hooks/useRowProps';
import { useTableTitle } from 'hooks/useTableTitle';
import intl from 'i18n/intl';
import { get, isEmpty } from 'lodash';
import { fetchFields } from 'slices/field.slice';
import { requestObjectTypes } from 'slices/objectTypes.slice';
// Slices
import {
  type TMappedOrganizationTreeNode,
  checkDrawerExist,
  createOrganizationNodeLocal,
  deleteOrganizationNode,
  fetchOrganizationHierarchy,
  handleCompare,
  onRowClick,
  organizationHierarchyLoading,
  organizationMapSelector,
  organizationTreeSelector,
  saveOrganizationNode,
  selectedOrganizationNodeIdsSelector,
} from 'slices/organizationHierarchy.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { countTreeNodes } from 'utils/treeData';
import type { TOrganizationNode, TOrganizationTreeNode } from '@timeedit/types';
import {
  Button,
  DefaultContentLayout,
  EditableTable,
  Icon,
} from '@timeedit/ui-components';
import {
  type MenuProps,
  Modal,
  Popover,
  Tooltip,
} from '@timeedit/ui-components/antd';
import type { ItemType } from '@timeedit/ui-components/antd/menu/interface';
import Drawer from './OrganizationDrawer';

const language = intl.messages as Record<string, string>;

const OrganizationNodes = () => {
  const dispatch = useAppDispatch();
  const [modal, contextHolder] = Modal.useModal();
  /**
   * SELECTORS
   */
  const organizationTree = useAppSelector(organizationTreeSelector);
  const loading = useAppSelector(organizationHierarchyLoading);
  const nodesMap = useAppSelector(organizationMapSelector);
  const highlightIds = useAppSelector(selectedOrganizationNodeIdsSelector);
  /**
   * MEMO
   */
  const [treeData, listParentIds] = useMemo(() => {
    if (isEmpty(organizationTree)) return [[], []];
    return handleConvert([organizationTree]);
  }, [organizationTree]);
  /**
   *  STATE
   */
  const {
    expandable,
    expandAll: hookExpandAll,
    collapseAll: hookCollapseAll,
  } = useExpandable({ initialKeys: listParentIds });

  const treeDataPopup = useMemo(
    () =>
      !isEmpty(organizationTree) ? convertDataPopup([organizationTree]) : [],
    [organizationTree]
  );
  /**
   * FUNCTIONS
   */

  const _onClickAddNew = useCallback(() => {
    dispatch(createOrganizationNodeLocal({}, treeData[0]));
  }, [treeData, dispatch]);

  const handleRowClick = useCallback(
    (record: TMappedOrganizationTreeNode) => {
      dispatch(onRowClick({ record }));
    },
    [dispatch]
  );

  const onRow = useRowProps(handleRowClick, highlightIds);

  const onCompare = useCallback(
    (record: TMappedOrganizationTreeNode) => {
      dispatch(handleCompare({ record }));
    },
    [dispatch]
  );

  const onDelete = useCallback(
    (record: TMappedOrganizationTreeNode) => {
      modal.confirm({
        title: `Are you sure you want to delete ${record?.title}?`,
        icon: <Icon id="ExclamationCircleOutlined" alt="Exclamation Circle" />,
        okText: 'OK',
        cancelText: 'Cancel',
        onOk: async () => {
          dispatch(
            deleteOrganizationNode({ organizationNodeId: record.value })
          );
        },
      });
    },
    [dispatch, modal]
  );

  const onMove = useCallback(
    (record: TMappedOrganizationTreeNode, destination?: string) => {
      if (destination) {
        const [idDes] = destination.split('#');
        const currentNode = nodesMap[record?.value];

        let parentNodeIds = currentNode.parentNodeIds;
        if (idDes === 'root') {
          parentNodeIds = [];
        } else {
          parentNodeIds = isEmpty(parentNodeIds)
            ? [idDes]
            : parentNodeIds.map((node: string) =>
                node === record.parentId ? idDes : node
              );
        }
        dispatch(
          saveOrganizationNode({
            organizationNode: {
              ...currentNode,
              parentNodeIds,
            },
          })
        );
      }
    },
    [dispatch, nodesMap]
  );

  const onClone = useCallback(
    (toLocation: TMappedOrganizationTreeNode, fromLocation?: string) => {
      if (fromLocation) {
        const [idDes] = fromLocation.split('#');
        if (
          idDes === 'root' ||
          toLocation.value === 'root' ||
          idDes === toLocation.id
        ) {
          return 0;
        }
        const currentNode = nodesMap[idDes];
        dispatch(
          saveOrganizationNode({
            organizationNode: {
              ...currentNode,
              parentNodeIds: currentNode.parentNodeIds.concat(toLocation.value),
            },
          })
        );
      }
    },
    [dispatch, nodesMap]
  );

  const onDeleteInstance = useCallback(
    (record: TMappedOrganizationTreeNode) => {
      modal.confirm({
        title: `Are you sure you want to delete instance ${record?.title}?`,
        icon: <Icon id="ExclamationCircleOutlined" alt="Exclamation Circle" />,
        okText: 'OK',
        cancelText: 'Cancel',
        onOk: async () => {
          const dataSave = nodesMap[record.value];
          const parentNodeIds = dataSave.parentNodeIds.filter(
            (i: string) => i !== record.parentId
          );
          dispatch(
            saveOrganizationNode({
              organizationNode: {
                ...dataSave,
                parentNodeIds,
              },
            })
          );
        },
      });
    },
    [dispatch, modal, nodesMap]
  );

  const onExpandAll = useCallback(
    (record: TMappedOrganizationTreeNode) => {
      hookExpandAll(record.id, getNestedChildIds(record));
    },
    [hookExpandAll]
  );

  const onCollapseAll = useCallback(
    (record: TMappedOrganizationTreeNode) => {
      hookCollapseAll(record.id, getNestedChildIds(record));
    },
    [hookCollapseAll]
  );

  const menuProps = useCallback(
    (record: TMappedOrganizationTreeNode): MenuProps => {
      const isDrawerOpen = dispatch(checkDrawerExist());
      return {
        items: renderItems({
          record,
          onMove,
          treeDataPopup,
          onClone,
          nodesMap,
          isDrawerOpen,
        }),
        onClick: (info) => {
          info.domEvent.stopPropagation();
          info.domEvent.nativeEvent.stopImmediatePropagation();
          switch (info.key) {
            case 'expandAll':
              onExpandAll(record);
              break;
            case 'collapseAll':
              onCollapseAll(record);
              break;
            case 'compare':
              onCompare(record);
              break;
            case 'create':
              dispatch(
                createOrganizationNodeLocal(nodesMap[record.value], record)
              );
              hookExpandAll(record.id, []);
              break;
            case 'delete':
              onDelete(record);
              break;
            case 'Remove_Instance':
              onDeleteInstance(record);
              break;
            default:
              break;
          }
        },
      };
    },
    [
      dispatch,
      nodesMap,
      onClone,
      onCollapseAll,
      onCompare,
      onDelete,
      onDeleteInstance,
      onExpandAll,
      hookExpandAll,
      onMove,
      treeDataPopup,
    ]
  );

  /**
   * EFFECT
   */
  useEffect(() => {
    dispatch(fetchOrganizationHierarchy());
    dispatch(requestObjectTypes());
    dispatch(fetchFields());
  }, [dispatch]);

  const footer = useCallback(() => {
    return (
      <Button size="small" type="primary" onClick={_onClickAddNew}>
        {language.create_new}
      </Button>
    );
  }, [_onClickAddNew]);

  const [contentRef, defaultTableProps] = useDefaultTableProps(10);

  const filterOrgsNum = useMemo(() => countTreeNodes(treeData), [treeData]);

  const totalOrgsNum = useMemo(
    () => countTreeNodes([organizationTree]),
    [organizationTree]
  );

  const title = useTableTitle<TMappedOrganizationTreeNode>({
    total: totalOrgsNum,
    filteredAmount: filterOrgsNum,
  });

  return (
    <DefaultContentLayout
      title="Organizations"
      subtitle="Organization Management"
    >
      <div className="inner-content--wrapper">
        <div
          ref={contentRef}
          style={{ margin: '10px 5px 0px', width: 'calc(100% - 10px)' }}
        >
          <EditableTable
            columns={columns}
            dataSource={treeData}
            loading={loading}
            onRow={onRow}
            expandable={expandable}
            footer={footer}
            getMenuProps={menuProps}
            title={title}
            {...defaultTableProps}
          />
          <Drawer />
        </div>
      </div>
      {contextHolder}
    </DefaultContentLayout>
  );
};

export default OrganizationNodes;

const handleConvert = (
  tree: TOrganizationTreeNode[],
  parentId?: string
): [TMappedOrganizationTreeNode[], string[]] => {
  const idCount: Record<string, number> = {};
  const listParentIds: string[] = [];
  const dataConvert = convertData(tree, parentId, idCount, listParentIds);
  return [dataConvert, listParentIds];
};

const convertData = (
  tree: TOrganizationTreeNode[],
  parentId?: string,
  idCount?: Record<string, number>,
  listParentIds?: string[]
): TMappedOrganizationTreeNode[] => {
  return tree.map((item) => {
    idCount[item.id] = idCount[item.id] ? idCount[item.id] + 1 : 1;
    const uniqueId = `${item.id}#${idCount[item.id]}`;
    if (item.children && listParentIds) {
      listParentIds.push(uniqueId);
    }
    const children = item.children
      ? convertData(item.children, item.id, idCount, listParentIds)
      : undefined;
    return {
      title: item.name,
      key: item.virtualId,
      value: item.id,
      id: uniqueId,
      parentId,
      children,
    } satisfies TMappedOrganizationTreeNode;
  });
};

type TOrganizationTreeNodePopupData = {
  title: string;
  key: string;
  value: string;
  children?: TOrganizationTreeNodePopupData[];
};

const convertDataPopup = (
  tree: TOrganizationTreeNode[]
): TOrganizationTreeNodePopupData[] => {
  return tree.map((item) => {
    const children = item.children
      ? convertDataPopup(item.children)
      : undefined;
    return {
      title: item.name,
      key: `${item.id}#${item.virtualId}`,
      value: `${item.id}#${item.virtualId}`,
      children,
    } satisfies TOrganizationTreeNodePopupData;
  });
};

const columns = [
  {
    title: 'Organizations',
    dataIndex: 'title',
    key: 'title',
  },
];

const deleteOps = {
  key: 'delete',
  label: <span style={{ color: 'red' }}>Delete</span>,
  icon: <Icon id="DeleteOutlined" alt="Delete" style={{ color: 'red' }} />,
};

const createOps = {
  key: 'create',
  label: 'Create new organization',
  icon: <Icon id="FileAddOutlined" alt="File Add" />,
};
const removeInstance = {
  key: 'Remove_Instance',
  label: <span style={{ color: 'red' }}>Delete Instance</span>,
  icon: <Icon id="DeleteOutlined" alt="Delete" style={{ color: 'red' }} />,
};
const expandAll = {
  key: 'expandAll',
  label: <span>Expand all below this node</span>,
  icon: <Icon id="ExpandAltOutlined" alt="Expand Alt" />,
};
const collapseAll = {
  key: 'collapseAll',
  label: <span>Collapse all below this node</span>,
  icon: <Icon id="ShrinkOutlined" alt="Shrink" />,
};
const removeInstanceDisable = {
  key: '',
  label: (
    <Tooltip title="Not an instance node">
      <div>Delete Instance</div>
    </Tooltip>
  ),
  icon: <Icon id="DeleteOutlined" alt="Delete" />,
  disabled: true,
};

type TRenderItemsOptions = {
  record: TMappedOrganizationTreeNode;
  onClone: (record: TMappedOrganizationTreeNode, destination?: string) => void;
  onMove: (record: TMappedOrganizationTreeNode, destination?: string) => void;
  nodesMap: Record<string, TOrganizationNode>;
  treeDataPopup: TOrganizationTreeNodePopupData[];
  isDrawerOpen?: boolean;
};

function renderItems({
  record,
  onMove,
  treeDataPopup,
  onClone,
  nodesMap,
  isDrawerOpen,
}: TRenderItemsOptions): ItemType[] {
  const moveOps = {
    key: 'move',
    label: (
      <Popover
        zIndex={10000}
        placement="right"
        content={
          <TreeComponent
            onSelect={(itemSelect: string[]) => onMove(record, itemSelect[0])}
            treeData={treeDataPopup}
            title={`Move organization to other node`}
          />
        }
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          Move organization to other node
          <Icon id="RightOutlined" alt="Right" style={{ fontSize: 12 }} />
        </div>
      </Popover>
    ),
    icon: <Icon id="SwapOutlined" alt="Swap" />,
  };
  const cloneOps = {
    key: 'clone_to',
    label: (
      <Popover
        zIndex={10000}
        placement="right"
        content={
          <TreeComponent
            onSelect={(itemSelect: string[]) => onClone(record, itemSelect[0])}
            treeData={treeDataPopup}
            title={`Add instance of existing organization`}
          />
        }
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          Add instance of existing organization
          <Icon id="RightOutlined" alt="Right" style={{ fontSize: 12 }} />
        </div>
      </Popover>
    ),
    icon: <Icon id="CopyOutlined" alt="Copy" />,
  };

  const headerAndCompare: ItemType[] = [
    {
      key: 'title',
      label: record.title,
      disabled: true,
    },
    {
      type: 'divider',
    },
    {
      key: 'compare',
      label: isDrawerOpen ? 'Compare' : 'Open',
      icon: <Icon id="EyeOutlined" alt="Eye" />,
    },
  ];
  const parentNodeIds = get(nodesMap[record.value], 'parentNodeIds', []);
  const expandedOps = isEmpty(record.children) ? [] : [collapseAll, expandAll];
  if (record.value === 'root') {
    return [createOps, ...expandedOps];
  }
  if (isEmpty(parentNodeIds)) {
    return [
      ...headerAndCompare,
      createOps,
      moveOps,
      cloneOps,
      deleteOps,
      removeInstanceDisable,
      ...expandedOps,
    ];
  }
  if (parentNodeIds.length === 1) {
    return [
      ...headerAndCompare,
      createOps,
      moveOps,
      cloneOps,
      deleteOps,
      removeInstanceDisable,
      ...expandedOps,
    ];
  }
  // if more than 1 parent
  if (parentNodeIds.length > 1) {
    return [
      ...headerAndCompare,
      createOps,
      moveOps,
      cloneOps,
      deleteOps,
      removeInstance,
      ...expandedOps,
    ];
  }
}

type TWithChildrenAndId = {
  id: string;
  children?: TWithChildrenAndId[];
};

function getNestedChildIds<T extends TWithChildrenAndId>(obj: T) {
  const result: string[] = [];
  getListChildId<T>(obj, result);
  return result;
}

function getListChildId<T extends TWithChildrenAndId>(
  obj: T,
  result: string[]
) {
  const listChild = obj?.children?.map((item) => item.id);
  if (listChild) {
    result.push(...listChild);
    obj?.children.forEach((child) => {
      if (obj?.children?.length > 0) {
        getListChildId(child, result);
      }
    });
  }
}
